import { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, LineElement, PointElement, LinearScale, CategoryScale, Filler } from 'chart.js';
import './index.css';
import './components/styles/ModularComponents.css';

// Import modular components
import { Header, Footer, LoadingSpinner, ErrorBoundary } from './components/Common';
import { ConsensusOverview, FlowConsensusReal } from './components/Consensus';
import { NetworkStats, BootstrapNodesList } from './components/Network';
import Dashboard from './components/Dashboard/Dashboard';
import GovernanceReal from './components/Governance/GovernanceReal';
import ValidatorsList from './components/Validators/ValidatorsList';
import DelegatorsList from './components/Delegators/DelegatorsList';

// Import API configuration
import { API_CONFIG } from './config/api';

ChartJS.register(ArcElement, Tooltip, Legend, LineElement, PointElement, LinearScale, CategoryScale, Filler);

// AVO Protocol RPC endpoint - use environment variable
const RPC_URL = API_CONFIG.RPC_URL;

// JSON-RPC helper function for AVO Protocol
const rpcCall = async (method, params = []) => {
  try {
    const response = await fetch(RPC_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        method,
        params,
        id: 1,
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    
    if (data.error) {
      throw new Error(data.error.message || 'RPC Error');
    }

    return data.result;
  } catch (error) {
    console.warn(`RPC call failed for ${method}:`, error.message);
    return null;
  }
};

const App = () => {
  // State management
  const [activeTab, setActiveTab] = useState('dashboard');
  const [isLoading, setIsLoading] = useState(true);
  const [chainData, setChainData] = useState({ chain: [], height: 0 });
  const [nodes, setNodes] = useState([]);
  const [supply, setSupply] = useState({ total: 0, circulating: 0 });
  const [consensusMetrics, setConsensusMetrics] = useState({});
  const [performanceMetrics, setPerformanceMetrics] = useState({});
  const [networkStats, setNetworkStats] = useState({ total_transactions: 0 });
  const [treasuryData, setTreasuryData] = useState(null);

  // Data fetching functions
  const fetchAllData = async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    try {
      // Fetch real network statistics
      const networkStatsResult = await rpcCall('avo_getNetworkStats');
      if (networkStatsResult) {
        // Save complete network stats
        setNetworkStats(networkStatsResult);
        
        // Map network stats to our state structure
        setChainData({
          chain: [], // We'll get this from block data
          height: parseInt(networkStatsResult.total_transactions?.toString() || '0', 10),
          chainId: networkStatsResult.chain_id || 1337
        });
        
        // Fetch real ZK optimization metrics
        let zkMetrics = {
          zk_proofs_generated: 0, // Start with 0, get real value from RPC
          constraint_reduction: 0.0,
          plonk_speedup: 0,
          recursive_proofs: 0,
          gpu_acceleration: 0,
          zk_vm_opcodes: 0
        };
        
        try {
          const zkResult = await rpcCall('avo_getZkMetrics');
          if (zkResult) {
            zkMetrics = {
              zk_proofs_generated: zkResult.overall_performance?.total_proofs_generated || 0,
              constraint_reduction: zkResult.constraint_batching?.reduction_percentage || 0.0,
              plonk_speedup: zkResult.hardware_acceleration?.speedup_factor || 0,
              recursive_proofs: zkResult.recursive_proofs?.proofs_aggregated || 0,
              gpu_acceleration: zkResult.hardware_acceleration?.speedup_factor || 0,
              zk_vm_opcodes: zkResult.zk_vm?.total_instructions || 0,
              proof_time_ms: zkResult.plonk_implementation?.proof_generation_time_ms || 0,
              memory_efficiency: "N/A", // Not available in current metrics
              circuit_success_rate: 1.0 // Assume success for now
            };
          }
        } catch (error) {
          console.log('ZK metrics not available, using fallback data');
        }
        
        setConsensusMetrics({
          active_validators: networkStatsResult.active_validators || 0,
          consensus_efficiency: networkStatsResult.consensus_efficiency || 0.98,
          current_epoch: networkStatsResult.current_epoch || 0,
          connected_peers: networkStatsResult.connected_peers || 0, // REAL from network stats
          total_nodes: networkStatsResult.active_validators || 0,
          difficulty: 'Dynamic Flow',
          zk_proofs_generated: zkMetrics.zk_proofs_generated,
          avg_tps: networkStatsResult.avg_tps || 0,
          // Add ZK optimization metrics to consensus
          zk_optimization: {
            constraint_reduction: zkMetrics.constraint_reduction,
            plonk_speedup: zkMetrics.plonk_speedup,
            recursive_proofs: zkMetrics.recursive_proofs,
            gpu_acceleration: zkMetrics.gpu_acceleration,
            zk_vm_opcodes: zkMetrics.zk_vm_opcodes,
            proof_time_ms: zkMetrics.proof_time_ms,
            memory_efficiency: zkMetrics.memory_efficiency,
            circuit_success_rate: zkMetrics.circuit_success_rate
          }
        });
        
        setPerformanceMetrics({
          real_tps: networkStatsResult.avg_tps || 0,
          throughput: networkStatsResult.avg_tps || 0,
          latency: 1500,
        });
        
        // Fetch real supply data using avo_getTotalSupply
        const supplyResult = await rpcCall('avo_getTotalSupply');
        console.log('Supply RPC Result:', supplyResult); // Debug log
        
        // Also get treasury balances to calculate real total
        const treasuryResult = await rpcCall('avo_getTreasuryBalances');
        
        if (supplyResult) {
          // Parse total_supply_avo which comes as a string with decimals
          const totalSupplyAvo = parseFloat(supplyResult.total_supply_avo || supplyResult.total_supply_formatted || '0');
          const circulatingSupplyAvo = parseFloat(supplyResult.circulating_supply_avo || '0');
          
          setSupply({
            total: totalSupplyAvo,
            circulating: circulatingSupplyAvo > 0 ? circulatingSupplyAvo : totalSupplyAvo * 0.85
          });
          console.log('Supply updated to:', totalSupplyAvo, 'AVO (circulating:', circulatingSupplyAvo, 'AVO)');
        } else if (treasuryResult) {
          // Fallback: Calculate total from treasury balances
          let totalFromTreasury = 0;
          for (const value of Object.values(treasuryResult)) {
            // Parse "123.456789 AVO" format
            const match = value.match(/([0-9,.]+)/);
            if (match) {
              totalFromTreasury += parseFloat(match[1].replace(/,/g, ''));
            }
          }
          setSupply({
            total: totalFromTreasury,
            circulating: totalFromTreasury * 0.85
          });
          console.log('Supply calculated from treasury:', totalFromTreasury, 'AVO');
        } else {
          // Last fallback to network stats
          setSupply({
            total: parseFloat(networkStatsResult.total_supply || '0') / 1e18,
            circulating: parseFloat(networkStatsResult.total_supply || '0') / 1e18 * 0.8
          });
        }
        
        // Set nodes from validators
        const nodeList = Array.from({length: networkStatsResult.active_validators || 0}, (_, i) => ({
          id: i + 1,
          status: 'active',
          shard: (i % 8) + 1 // Distribute across 8 shards
        }));
        setNodes(nodeList);
        
        // Set treasury data (already fetched above for supply calculation)
        if (treasuryResult) {
          setTreasuryData(treasuryResult);
        }
      }

      // Get real block number and block data
      const blockNumberResult = await rpcCall('eth_blockNumber');
      if (blockNumberResult) {
        const blockHeight = parseInt(blockNumberResult, 16);
        
        // Try to get real blocks with merkle roots
        const recentBlocksResult = await rpcCall('avo_getRecentBlocks', [Math.min(blockHeight, 20)]);
        console.log('Raw RPC result:', recentBlocksResult); // Debug log
        if (recentBlocksResult && recentBlocksResult.blocks) {
          console.log('First block data:', recentBlocksResult.blocks[0]); // Debug log
          setChainData(prev => ({
            ...prev,
            height: blockHeight,
            chain: recentBlocksResult.blocks.map(block => {
              const merkleRoot = block.merkle_root || block.merkleRoot || '0x0000000000000000000000000000000000000000000000000000000000000000';
              console.log('Block merkle_root:', block.merkle_root, 'Final:', merkleRoot); // Debug log
              return {
                index: block.height || block.block_number || 0,
                hash: block.hash || block.block_hash || '0x0000000000000000000000000000000000000000000000000000000000000000',
                timestamp: block.timestamp || block.block_timestamp || Math.floor(Date.now() / 1000),
                transactions: block.transactions || [],
                merkle_root: merkleRoot
              };
            })
          }));
          console.log('Real blocks with merkle roots loaded:', recentBlocksResult.blocks.length);
        } else {
          // Fallback to mock data if real blocks not available
          setChainData(prev => ({
            ...prev,
            height: blockHeight,
            chain: Array.from({length: Math.min(blockHeight, 20)}, (_, i) => ({
              index: blockHeight - i,
              hash: `0x${Math.random().toString(16).slice(2, 66).padStart(64, '0')}`,
              timestamp: Math.floor(Date.now() / 1000) - (i * 3), // 3 second blocks
              transactions: Array.from({length: Math.floor(Math.random() * 10)}, (_, j) => ({
                hash: `0x${Math.random().toString(16).slice(2, 66).padStart(64, '0')}`,
                amount: Math.random() * 100
              })),
              merkle_root: `0x${Math.random().toString(16).slice(2, 66).padStart(64, '0')}` // Mock merkle root
            }))
          }));
          console.log('Using mock blocks (real blocks not available)');
        }
      }

      // Get real peer count
      const peerCountResult = await rpcCall('net_peerCount');
      if (peerCountResult) {
        const peerCount = parseInt(peerCountResult, 16);
        setConsensusMetrics(prev => ({
          ...prev,
          connected_peers: peerCount
        }));
      }

      // Get real chain ID
      const chainIdResult = await rpcCall('eth_chainId');
      if (chainIdResult) {
        const chainId = parseInt(chainIdResult, 16);
        setChainData(prev => ({
          ...prev,
          chainId: chainId
        }));
      }

      // Note: Validator/Sharding data removed - using simpler ValidatorsList component now

    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  };

  // Auto-refresh data every 30 seconds
  useEffect(() => {
    fetchAllData(); // Initial load with loading indicator
    
    const interval = setInterval(() => {
      fetchAllData(false); // Background updates without loading indicator
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ErrorBoundary>
      <div className="app">
        <Header isLoading={isLoading} />
        
        {/* Navigation Tabs */}
        <nav className="nav-tabs">
          <button 
            className={`nav-tab ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          
          <button 
            className={`nav-tab ${activeTab === 'consensus' ? 'active' : ''}`}
            onClick={() => setActiveTab('consensus')}
          >
            Consensus
          </button>
          
          <button 
            className={`nav-tab ${activeTab === 'network' ? 'active' : ''}`}
            onClick={() => setActiveTab('network')}
          >
            Network
          </button>
          
          <button 
            className={`nav-tab ${activeTab === 'bootstrap' ? 'active' : ''}`}
            onClick={() => setActiveTab('bootstrap')}
          >
            Bootstrap Nodes
          </button>
          
          <button 
            className={`nav-tab ${activeTab === 'validators' ? 'active' : ''}`}
            onClick={() => setActiveTab('validators')}
          >
            Validators
          </button>
          
          <button 
            className={`nav-tab ${activeTab === 'delegators' ? 'active' : ''}`}
            onClick={() => setActiveTab('delegators')}
          >
            Delegators
          </button>
          
          <button
            className={`nav-tab ${activeTab === 'governance' ? 'active' : ''}`}
            onClick={() => setActiveTab('governance')}
          >
            Governance
          </button>
        </nav>

        <main className="main-content">
          {isLoading && <LoadingSpinner />}

          {!isLoading && (
            <>
              {activeTab === 'dashboard' && (
                <Dashboard 
                  chainData={chainData}
                  supply={supply}
                  consensusMetrics={consensusMetrics}
                  performanceMetrics={performanceMetrics}
                  networkStats={networkStats}
                />
              )}
              
              {activeTab === 'consensus' && (
                <div className="dashboard-card">
                  <ConsensusOverview 
                    consensusMetrics={consensusMetrics}
                    chainData={chainData}
                  />
                  <FlowConsensusReal />
                </div>
              )}
              
              {activeTab === 'network' && (
                <div className="dashboard-card">
                  <NetworkStats 
                    chainData={chainData} 
                    nodes={nodes}
                    consensusMetrics={consensusMetrics}
                  />
                </div>
              )}
              
              {activeTab === 'bootstrap' && (
                <BootstrapNodesList rpcUrl={RPC_URL} />
              )}
              
              {activeTab === 'validators' && (
                <ValidatorsList rpcUrl={RPC_URL} />
              )}
              
              {activeTab === 'delegators' && (
                <DelegatorsList rpcUrl={RPC_URL} />
              )}
              
              {activeTab === 'governance' && (
                <GovernanceReal />
              )}
            </>
          )}
        </main>

        <Footer />
      </div>
    </ErrorBoundary>
  );
};

export default App;